import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { SitePages } from "@/lib/api";
import { Monitor, Smartphone, Tablet, RefreshCw, ExternalLink, X, ChevronsRight, ChevronsLeft } from "lucide-react";

/**
 * Persistent live-preview pane shown alongside every admin route.
 *
 * Features:
 *  - URL switcher (Home / Packages / any custom Site Page)
 *  - Viewport size selector (desktop / tablet / mobile)
 *  - Manual refresh + open-in-new-tab
 *  - Auto-refresh when any editor dispatches the
 *    `admin-preview-refresh` window event after a save
 *  - Width is user-resizable via a vertical drag handle (persisted)
 *  - Collapsible (state persisted in localStorage)
 *  - Auto-syncs to the admin section being edited (e.g., editing
 *    a site page focuses the preview to /p/<slug>)
 */
const VIEWPORTS = {
  desktop: { width: 1280, label: "Desktop", icon: Monitor },
  tablet:  { width: 820,  label: "Tablet",  icon: Tablet },
  mobile:  { width: 390,  label: "Mobile",  icon: Smartphone },
};

const LS_OPEN  = "atc.admin.preview.open";
const LS_WIDTH = "atc.admin.preview.width";
const LS_URL   = "atc.admin.preview.url";
const LS_VP    = "atc.admin.preview.vp";

const DEFAULT_URLS = [
  { url: "/",         label: "Home",          group: "Public" },
  { url: "/packages", label: "Packages",      group: "Public" },
];

const GlobalPreviewPane = () => {
  const location = useLocation();
  const iframeRef = useRef(null);

  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) !== "0");
  const [width, setWidth] = useState(() => Number(localStorage.getItem(LS_WIDTH)) || 520);
  const [previewUrl, setPreviewUrl] = useState(() => localStorage.getItem(LS_URL) || "/");
  const [vp, setVp] = useState(() => localStorage.getItem(LS_VP) || "desktop");
  const [iframeKey, setIframeKey] = useState(0);
  const [sitePages, setSitePages] = useState([]);

  const cfg = VIEWPORTS[vp] || VIEWPORTS.desktop;

  // Persist
  useEffect(() => { localStorage.setItem(LS_OPEN, open ? "1" : "0"); }, [open]);
  useEffect(() => { localStorage.setItem(LS_WIDTH, String(width)); }, [width]);
  useEffect(() => { localStorage.setItem(LS_URL, previewUrl); }, [previewUrl]);
  useEffect(() => { localStorage.setItem(LS_VP, vp); }, [vp]);

  // Load site page URLs for the switcher
  const loadSitePages = useCallback(async () => {
    try {
      const pages = await SitePages.list(false);
      setSitePages(pages.filter((p) => p.is_published).map((p) => ({
        url: `/p/${p.slug}`,
        label: p.title,
        group: "Site pages",
      })));
    } catch { /* not authed yet */ }
  }, []);
  useEffect(() => { loadSitePages(); }, [loadSitePages, location.pathname]);

  // Listen for save events from any editor
  useEffect(() => {
    const handler = () => {
      setIframeKey((k) => k + 1);
      loadSitePages();
    };
    window.addEventListener("admin-preview-refresh", handler);
    return () => window.removeEventListener("admin-preview-refresh", handler);
  }, [loadSitePages]);

  // Auto-focus preview based on admin route
  useEffect(() => {
    if (location.pathname.startsWith("/admin/packages")) {
      if (previewUrl !== "/packages") setPreviewUrl("/packages");
    } else if (location.pathname === "/admin/pages" || location.pathname === "/admin") {
      // Stay on whatever URL the user picked (don't override their selection)
    }
    // Site-page-aware navigation deliberately skipped here — managers can call
    // window.__setAdminPreviewUrl(url) if needed in the future.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Resize handle drag
  const resizing = useRef(false);
  useEffect(() => {
    const onMove = (e) => {
      if (!resizing.current) return;
      const next = Math.max(360, Math.min(900, window.innerWidth - e.clientX));
      setWidth(next);
    };
    const onUp = () => { resizing.current = false; document.body.style.cursor = ""; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 px-2.5 py-4 rounded-l-xl bg-slate-900/95 border border-r-0 border-white/10 text-slate-300 hover:text-white hover:bg-slate-800 shadow-2xl"
        title="Show live preview"
        data-testid="admin-preview-open"
      >
        <ChevronsLeft className="h-4 w-4" />
      </button>
    );
  }

  // Scale = preview pane content width / target viewport width
  const innerContentWidth = width - 2; // account for border
  const scale = Math.min(1, innerContentWidth / cfg.width);
  const allUrls = [...DEFAULT_URLS, ...sitePages];
  const currentLabel = allUrls.find((u) => u.url === previewUrl)?.label || previewUrl;

  return (
    <>
      <aside
        className="shrink-0 bg-slate-950 border-l border-white/5 flex flex-col relative"
        style={{ width }}
        data-testid="admin-preview-pane"
      >
        {/* Drag handle */}
        <div
          onMouseDown={() => { resizing.current = true; document.body.style.cursor = "col-resize"; }}
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-atc-blue/30 transition z-10"
          title="Drag to resize"
        />

        {/* Header */}
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 shrink-0">
          <span className="h-2 w-2 rounded-full bg-rose-500/60" />
          <span className="h-2 w-2 rounded-full bg-amber-500/60" />
          <span className="h-2 w-2 rounded-full bg-emerald-500/60" />

          <select
            value={previewUrl}
            onChange={(e) => setPreviewUrl(e.target.value)}
            className="ml-2 flex-1 min-w-0 bg-slate-900 border border-white/10 rounded-md text-xs text-slate-200 px-2 py-1 focus:outline-none focus:border-atc-blue"
            data-testid="admin-preview-url"
            title={currentLabel}
          >
            <optgroup label="Public">
              {DEFAULT_URLS.map((u) => (
                <option key={u.url} value={u.url}>{u.label}</option>
              ))}
            </optgroup>
            {sitePages.length > 0 && (
              <optgroup label="Site pages">
                {sitePages.map((u) => (
                  <option key={u.url} value={u.url}>{u.label}</option>
                ))}
              </optgroup>
            )}
          </select>

          <div className="flex items-center gap-0.5">
            {Object.entries(VIEWPORTS).map(([key, v]) => {
              const Icon = v.icon;
              const active = vp === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setVp(key)}
                  className={`p-1.5 rounded-md transition ${
                    active ? "bg-atc-blue/20 text-atc-blue" : "text-slate-400 hover:text-white"
                  }`}
                  title={v.label}
                  data-testid={`admin-preview-vp-${key}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-0.5 border-l border-white/10 ml-1 pl-1">
            <button
              type="button"
              onClick={() => setIframeKey((k) => k + 1)}
              className="p-1.5 rounded-md text-slate-400 hover:text-white"
              title="Refresh preview"
              data-testid="admin-preview-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 rounded-md text-slate-400 hover:text-white"
              title="Open in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md text-slate-400 hover:text-white"
              title="Hide preview"
              data-testid="admin-preview-hide"
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Iframe content — natural viewport width, scaled visually */}
        <div className="flex-1 overflow-hidden bg-slate-950 relative">
          <div
            style={{
              width: cfg.width,
              height: `calc((100vh - 41px) / ${scale})`,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <iframe
              ref={iframeRef}
              key={`${iframeKey}-${previewUrl}`}
              src={previewUrl + (previewUrl.includes("?") ? "&" : "?") + "__preview=1"}
              title="Live admin preview"
              className="w-full h-full block bg-slate-950"
              style={{ border: 0 }}
            />
          </div>
        </div>

        <div className="px-3 py-1.5 border-t border-white/10 text-[10px] text-slate-500 shrink-0 flex items-center justify-between">
          <span>Auto-refreshes after save</span>
          <span className="text-slate-600">{cfg.width}px @ {Math.round(scale * 100)}%</span>
        </div>
      </aside>
    </>
  );
};

export default GlobalPreviewPane;

/* Convenience helper — any editor can call this after a successful save
   to trigger an immediate iframe reload in the global preview pane. */
export const notifyPreviewRefresh = () => {
  window.dispatchEvent(new CustomEvent("admin-preview-refresh"));
};
